/**
 * Single source of truth for derived course status.
 *
 * The schema currently exposes three flags that can express the same idea
 * in different ways:
 *   - `is_published` (boolean)        → editorial visibility
 *   - `is_coming_soon` (boolean)      → marketing flag for upcoming courses
 *   - `syllabus.status` (string)      → legacy flag, still used in some rows
 *
 * To avoid scattered conditionals across the UI, derive a single status
 * from these flags using `getCourseStatus()`.
 *
 * NOTE: We intentionally do NOT change the underlying columns. The editor
 * still writes to the original toggles. This helper is read-only.
 */

export type CourseStatus = "draft" | "coming_soon" | "live";

export interface CourseStatusInput {
  is_published?: boolean | null;
  is_coming_soon?: boolean | null;
  syllabus?: unknown;
}

const isComingSoonSyllabus = (syllabus: unknown): boolean => {
  if (!syllabus || typeof syllabus !== "object") return false;
  return (syllabus as { status?: string }).status === "coming_soon";
};

export function getCourseStatus(course: CourseStatusInput): CourseStatus {
  // Unpublished always wins → draft (admins can preview separately)
  if (course.is_published === false) return "draft";

  // Coming soon: explicit flag OR legacy syllabus.status
  if (course.is_coming_soon === true || isComingSoonSyllabus(course.syllabus)) {
    return "coming_soon";
  }

  return "live";
}

/**
 * Returns true if the editor flags are in a contradictory state
 * (published + coming soon, or published flag mismatches syllabus).
 * Used to surface a warning in the admin editor.
 */
export function hasStatusConflict(course: CourseStatusInput): boolean {
  const syllabusComing = isComingSoonSyllabus(course.syllabus);
  const flagComing = course.is_coming_soon === true;
  // Conflict: one says coming soon, the other doesn't
  return syllabusComing !== flagComing;
}

/** Human label for badges. */
export function getCourseStatusLabel(status: CourseStatus): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "coming_soon":
      return "Coming Soon";
    case "live":
      return "Live";
  }
}
